import React, { useState, useEffect } from 'react';
import { Card, Button, Radio, InputNumber, message, Modal, Upload, Form, Input, Row, Col, Tag, Divider, Alert } from 'antd';
import { ShoppingCartOutlined, CreditCardOutlined, CheckCircleOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { QRCodeCanvas } from 'qrcode.react';

// 可靠的小程序环境检测函数
// 注意：非小程序环境（APP WebView / 普通浏览器）里 wx.miniProgram.getEnv 的回调不会触发，
// 若无超时兜底，await 会永远卡住 → 购买时一直转圈、拉不起支付。这里加 800ms 兜底。
async function ensureWeixinEnv() {
  if (!window.wx) {
    await new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
      s.onload = resolve;
      s.onerror = () => resolve(); // 忽略失败，后面会有兜底
      document.head.appendChild(s);
      setTimeout(resolve, 1500); // 脚本加载兜底，避免加载慢卡住
    });
  }
  return new Promise((resolve) => {
    let done = false;
    const ua = navigator.userAgent || '';
    const fallback = document.referrer.includes('servicewechat.com') || /miniProgram/i.test(ua);
    const finish = (v) => { if (!done) { done = true; resolve(v); } };
    const timer = setTimeout(() => finish(fallback), 800); // 关键兜底：getEnv 回调不触发时也能返回
    try {
      if (window.wx && window.wx.miniProgram && window.wx.miniProgram.getEnv) {
        window.wx.miniProgram.getEnv((res) => { clearTimeout(timer); finish(!!(res && res.miniprogram)); });
      } else {
        clearTimeout(timer);
        finish(fallback);
      }
    } catch {
      clearTimeout(timer);
      finish(false);
    }
  });
}

// 配置axios默认URL：使用相对路径以适配同域(https)与小程序web-view
axios.defaults.baseURL = '/api';

const CardSales = () => {
  const { user, token, setUser, setToken, handleWeChatLogin } = useAuthStore();
  const [cardPrices, setCardPrices] = useState([]);
  const [paymentConfigs, setPaymentConfigs] = useState([]);
  const [selectedCardType, setSelectedCardType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [form] = Form.useForm();
  const [miniProgramOpenid, setMiniProgramOpenid] = useState(null);

  useEffect(() => {
    fetchCardPrices();
    fetchPaymentConfigs();
    
    // 检查URL参数中的openid（用于支付）
    const urlParams = new URLSearchParams(window.location.search);
    const openid = urlParams.get('openid');
    if (openid) {
      setMiniProgramOpenid(openid);
    }
    
    // 从localStorage获取openid（如果URL中没有）
    if (!openid) {
      const storedOpenid = localStorage.getItem('wechat_openid');
      console.log('从localStorage获取openid:', storedOpenid);
      if (storedOpenid) {
        setMiniProgramOpenid(storedOpenid);
      }
    } else {
      console.log('从URL参数获取openid:', openid);
    }
    
    // 监听小程序消息
    const handleMessage = (event) => {
      console.log('收到小程序消息:', event.data);
      
      if (event.data && event.data.action === 'login') {
        const { openid, token: miniToken, userInfo } = event.data;
        console.log('收到小程序登录信息:', { openid, miniToken, userInfo });
        
        // 使用authStore的微信登录处理方法
        if (miniToken && userInfo) {
          const result = handleWeChatLogin({
            token: miniToken,
            user: userInfo
          });
          
          if (result.success) {
            // 保存openid到localStorage
            localStorage.setItem('wechat_openid', openid);
            setMiniProgramOpenid(openid);
            message.success('登录状态已同步');
          } else {
            message.error('登录状态同步失败');
          }
        }
      }
    };
    
    // 监听来自小程序的消息
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const fetchCardPrices = async () => {
    try {
      const response = await axios.get('/card-sales/card-prices');
      if (response.data.success) {
        setCardPrices(response.data.prices);
        if (response.data.prices.length > 0) {
          setSelectedCardType(response.data.prices[0].card_type);
        }
      }
    } catch (error) {
      console.error('获取卡密价格失败:', error);
      message.error('获取卡密价格失败');
    }
  };

  const fetchPaymentConfigs = async () => {
    try {
      // 暂时仅保留微信支付
      const configs = [{ payment_type: 'wechat', account_name: '微信支付' }];
      setPaymentConfigs(configs);
      setPaymentMethod('wechat');
    } catch (error) {
      console.error('获取支付配置失败:', error);
      message.error('获取支付配置失败');
    }
  };

  const handlePurchase = async () => {
    if (!selectedCardType || !paymentMethod) {
      message.error('请选择卡密类型和支付方式');
      return;
    }

    if (!token) {
      message.error('请先登录');
      return;
    }

    setLoading(true);
    try {
      // APP WebView：走原生微信支付（网页拿 APP 支付参数 → 交给 RN 原生 SDK 拉起微信）
      if (typeof window !== 'undefined' && window.IS_APP_WEBVIEW && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        try {
          const response = await axios.post('/card-sales/orders', {
            card_type: selectedCardType,
            quantity: quantity,
            payment_method: paymentMethod,
            platform: 'app',
          }, { headers: { 'Authorization': `Bearer ${token}` } });

          if (response.data.success && response.data.payment && response.data.payment.paymentParams) {
            const order = response.data.order;
            setCurrentOrder(order);
            // 兼容旧版APP：新版APP收到后会立刻回 app_pay_ack；4秒内没回 = 旧包，提示更新
            let acked = false;
            const onAck = () => { acked = true; };
            window.addEventListener('app_pay_ack', onAck, { once: true });
            setTimeout(() => {
              window.removeEventListener('app_pay_ack', onAck);
              if (!acked) message.warning('若未弹出微信支付，请将 APP 更新到最新版本后重试');
            }, 4000);

            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'WECHAT_PAY',
              paymentParams: response.data.payment.paymentParams,
              orderId: order.id,
              orderNo: order.order_no,
            }));
            message.info('正在拉起微信支付…');
            startPaymentStatusPolling(order.id); // 支付结果由后端回调置 paid，这里轮询兜底
          } else {
            message.error(response.data.message || '创建支付失败，请稍后重试');
          }
        } catch (e) {
          message.error(e?.response?.data?.message || '发起支付失败');
        } finally {
          setLoading(false);
        }
        return;
      }

      // 使用可靠的小程序环境检测
      const isInMiniProgram = await ensureWeixinEnv();
      
      console.log('环境检测结果:', {
        isInMiniProgram,
        userAgent: window.navigator.userAgent,
        referrer: document.referrer
      });
      
      // 在小程序web-view中，直接跳转到小程序支付页面
      if (isInMiniProgram) {
        console.log('检测到小程序web-view环境，准备创建订单');
        
        try {
          // 先创建订单
          const response = await axios.post('/card-sales/orders', {
            card_type: selectedCardType,
            quantity: quantity,
            payment_method: paymentMethod,
            platform: 'miniprogram',
            openid: undefined // 由后端从session或其他方式获取
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'User-ID': user?.id,
              'User-Role': user?.role
            }
          });

          if (response.data.success) {
            const order = response.data.order;
            const payment = response.data.payment;
            
            console.log('订单创建成功，准备通知小程序支付');
            console.log('订单信息:', order);
            console.log('支付信息:', payment);
            
            // 直接在web-view中调用小程序的支付接口
             // 先获取支付参数，传递openid
             console.log('支付时使用的openid:', miniProgramOpenid);
             const paymentResponse = await axios.post('/card-sales/mini-payment', {
               orderId: order.id,
               orderNo: order.order_no,
               openid: miniProgramOpenid // 使用小程序传递的openid
             }, {
               headers: {
                 'Authorization': `Bearer ${token}`,
                 'User-ID': user?.id,
                 'User-Role': user?.role
               }
             });
            
            if (paymentResponse.data.success && paymentResponse.data.paymentParams) {
              console.log('获取支付参数成功，跳转到支付页面');
              
              // 跳转到小程序的支付页面
              if (window.wx && window.wx.miniProgram && window.wx.miniProgram.navigateTo) {
                console.log('准备跳转到支付页面:', `/pages/payment/payment?orderId=${order.id}&orderNo=${order.order_no}`);
                
                window.wx.miniProgram.navigateTo({
                  url: `/pages/payment/payment?orderId=${order.id}&orderNo=${order.order_no}`,
                  success: (res) => {
                    console.log('跳转支付页面成功:', res);
                  },
                  fail: (err) => {
                    console.error('跳转支付页面失败:', err);
                    message.error('跳转支付页面失败: ' + err.errMsg);
                  }
                });
              } else {
                // 兜底方案：使用postMessage
                window.wx.miniProgram.postMessage({
                  data: {
                    action: 'payment',
                    orderId: order.id,
                    orderNo: order.order_no,
                    paymentParams: paymentResponse.data.paymentParams
                  }
                });
              }
              
              message.success('正在跳转支付...');
              setLoading(false);
              return;
            } else {
              message.error(paymentResponse.data.message || '获取支付参数失败');
              setLoading(false);
              return;
            }
          } else {
            message.error(response.data.message || '创建订单失败');
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('小程序订单创建失败:', error);
          const msg = error?.response?.data?.message || error?.message || '创建订单失败';
          message.error(msg);
          setLoading(false);
          return;
        }
      }
      
      // 非小程序环境，使用传统方式（再次确认）
      if (!(await ensureWeixinEnv())) {
        const response = await axios.post('/card-sales/orders', {
          card_type: selectedCardType,
          quantity: quantity,
          payment_method: paymentMethod,
          platform: 'web',
          openid: undefined
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'User-ID': user?.id,
            'User-Role': user?.role
          }
        });

        if (response.data.success) {
          const order = response.data.order;
          const payment = response.data.payment;
          
          // 合并订单和支付信息
          const orderWithPayment = {
            ...order,
            code_url: payment?.qrCode || null,
            out_trade_no: payment?.outTradeNo || null,
            test_mode: payment?.testMode || false,
            payment_type: payment?.type || null
          };
          
          setCurrentOrder(orderWithPayment);
          
          // 如果有支付二维码，显示支付模态框
          console.log('检查支付条件:', { payment: !!payment, qrCode: payment?.qrCode });
          if (payment && payment.qrCode) {
            console.log('设置模态框可见');
            setOrderModalVisible(true);
            message.success('订单创建成功，请扫码支付');
            
            // 开始轮询支付状态
            startPaymentStatusPolling(order.id);
          } else {
            console.log('支付二维码条件不满足');
            message.error('生成支付二维码失败，请联系客服');
          }
        }
        return;
      }
      
      // 若这里被识别为小程序，直接返回（不走二维码）
      return;
    } catch (error) {
      console.error('创建订单失败:', error);
      if (error.response?.status === 401) {
        message.error('请先登录');
      } else {
        const msg = error?.response?.data?.message || error?.message || '创建订单失败';
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 轮询支付状态
  const startPaymentStatusPolling = (orderId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/card-sales/orders/${orderId}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'User-ID': user?.id,
            'User-Role': user?.role
          }
        });

        if (response.data.success) {
          if (response.data.status === 'paid') {
            clearInterval(pollInterval);
            message.success('支付成功！卡密已生成，请查看"我的订单"');
            setOrderModalVisible(false);
            // 跳转到我的订单页面
            window.location.href = '/my-orders';
          } else if (response.data.status === 'failed') {
            clearInterval(pollInterval);
            message.error('支付失败');
          }
          // pending状态继续轮询
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
      }
    }, 3000); // 每3秒查询一次

    // 30分钟后停止轮询
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 30 * 60 * 1000);
  };

  const handleUploadProof = async (file) => {
    if (!currentOrder) return;

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('payment_proof', file);
    formData.append('payment_note', form.getFieldValue('payment_note') || '');

    try {
      const response = await axios.post(
        `/api/card-sales/orders/${currentOrder.id}/payment-proof`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
            'User-ID': user?.id,
            'User-Role': user?.role
          },
        }
      );

      if (response.data.success) {
        message.success('支付凭证上传成功');
        setOrderModalVisible(false);
      }
    } catch (error) {
      console.error('上传支付凭证失败:', error);
      message.error('上传支付凭证失败');
    } finally {
      setUploadLoading(false);
    }
  };

  const getCardIcon = (cardType) => {
    switch (cardType) {
      case 'basic': return '⭐';
      case 'vip': return '👑';
      case 'premium': return '💎';
      default: return '💳';
    }
  };

  const getCardName = (cardType) => {
    switch (cardType) {
      case 'basic': return '基础会员';
      case 'vip': return 'VIP会员';
      case 'premium': return '高级会员';
      default: return '会员卡';
    }
  };

  const getCardDays = (cardType) => {
    switch (cardType) {
      case 'basic': return '30天';
      case 'vip': return '';
      case 'premium': return '365天';
      default: return '30天';
    }
  };

  const getPaymentMethodName = (method) => {
    switch (method) {
      case 'wechat': return '微信支付';
      case 'alipay': return '支付宝';
      default: return method;
    }
  };

  const selectedPrice = cardPrices.find(p => p.card_type === selectedCardType);
  const totalAmount = selectedPrice ? selectedPrice.price * quantity : 0;

  return (
    <div style={{ padding: '6px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card 
        title="💳 购买会员卡" 
        style={{ 
          marginBottom: '20px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
          border: 'none'
        }}
        bodyStyle={{ padding: '6px' }}
      >
        <Row gutter={[24, 24]}>
          {/* 卡密类型选择 */}
          <Col xs={24} lg={16}>
            <Card 
              title="选择会员类型" 
              size="small"
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '6px' }}
            >
              <Radio.Group 
                value={selectedCardType} 
                onChange={(e) => setSelectedCardType(e.target.value)}
                style={{ width: '100%' }}
              >
                <Row gutter={[16, 16]}>
                  {cardPrices.map(price => (
                    <Col xs={24} sm={8} key={price.card_type}>
                      <Radio value={price.card_type} style={{ width: '100%' }}>
                        <Card 
                          hoverable
                          style={{ 
                            width: '100%',
                            border: selectedCardType === price.card_type ? '2px solid #1890ff' : '1px solid #d9d9d9',
                            backgroundColor: selectedCardType === price.card_type ? '#f0f8ff' : '#fff',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            background: selectedCardType === price.card_type ? 'linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%)' : 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            transition: 'all 0.3s ease'
                          }}
                          bodyStyle={{ padding: '6px' }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                              {getCardIcon(price.card_type)}
                            </div>
                            <h4 style={{ margin: '0 0 8px 0' }}>
                              {getCardName(price.card_type)}
                            </h4>
                            {getCardDays(price.card_type) && (
                              <p style={{ color: '#666', margin: '0 0 12px 0' }}>
                                {getCardDays(price.card_type)}有效期
                              </p>
                            )}
                            <div>
                              <span style={{ fontSize: '20px', color: '#1890ff', fontWeight: 'bold' }}>
                                ¥{price.price}
                              </span>
                              {price.original_price && (
                                <span style={{ 
                                  fontSize: '14px', 
                                  color: '#999', 
                                  textDecoration: 'line-through',
                                  marginLeft: '8px'
                                }}>
                                  ¥{price.original_price}
                                </span>
                              )}
                            </div>
                            {price.description && (
                              <p style={{ fontSize: '12px', color: '#999', margin: '8px 0 0 0' }}>
                                {price.description}
                              </p>
                            )}
                          </div>
                        </Card>
                      </Radio>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Card>
          </Col>

          {/* 购买信息 */}
          <Col xs={24} lg={8}>
            <Card title="购买信息" size="small">
              <div style={{ marginBottom: '16px' }}>
                <label>购买数量：</label>
                <InputNumber
                  min={1}
                  max={10}
                  value={quantity}
                  onChange={setQuantity}
                  style={{ width: '100%', marginTop: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label>支付方式：</label>
                <Radio.Group 
                  value={paymentMethod} 
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  {paymentConfigs.map(config => (
                    <Radio key={config.payment_type} value={config.payment_type} style={{ width: '100%', marginBottom: '8px' }}>
                      {getPaymentMethodName(config.payment_type)}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ fontSize: '16px' }}>总计：</span>
                  <span style={{ fontSize: '24px', color: '#ff4d4f', fontWeight: 'bold' }}>
                    ¥{totalAmount.toFixed(2)}
                  </span>
                </div>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={handlePurchase}
                  loading={loading}
                  style={{ width: '100%' }}
                >
                  立即购买
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 订单支付模态框 */}
      <Modal
        title="订单支付"
        open={orderModalVisible}
        onCancel={() => setOrderModalVisible(false)}
        footer={null}
        width={600}
      >
        {currentOrder && (
          <div>
            <Alert
              message="订单创建成功"
              description={`订单号：${currentOrder.order_no}`}
              type="success"
              showIcon
              style={{ marginBottom: '20px' }}
            />

            <Card size="small" style={{ marginBottom: '20px' }}>
              <Row gutter={[16, 8]}>
                <Col span={12}><strong>卡密类型：</strong>{getCardName(currentOrder.card_type)}</Col>
                <Col span={12}><strong>购买数量：</strong>{currentOrder.quantity}</Col>
                <Col span={12}><strong>单价：</strong>¥{currentOrder.unit_price}</Col>
                <Col span={12}><strong>总金额：</strong>¥{currentOrder.total_amount}</Col>
                <Col span={12}><strong>支付方式：</strong>{getPaymentMethodName(currentOrder.payment_method)}</Col>
                <Col span={12}><strong>订单状态：</strong><Tag color="orange">待支付</Tag></Col>
              </Row>
            </Card>

            <Card title="扫码支付" size="small" style={{ marginBottom: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}>
                  <strong>支付金额：¥{currentOrder.total_amount}</strong>
                </div>
                
                {/* 支付二维码 */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: '200px',
                  border: '1px dashed #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa'
                }}>
                  {console.log('模态框中检查 code_url:', currentOrder.code_url)}
                  {console.log('code_url 是否以 data:image 开头:', currentOrder.code_url?.startsWith('data:image'))}
                  {currentOrder.code_url ? (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', marginBottom: '15px', color: '#333' }}>
                        {currentOrder.payment_type === 'native' ? '请使用微信扫描下方二维码完成支付' : '请使用支付宝扫描下方二维码完成支付'}
                      </div>
                      
                      
                      {/* 统一用前端生成二维码，支持“长按识别/保存后识别” */}
                      {currentOrder.code_url.startsWith('data:image') ? (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ marginBottom: '12px' }}>
                            <img
                              src={currentOrder.code_url}
                              alt="支付二维码"
                              style={{
                                maxWidth: '220px',
                                maxHeight: '220px',
                                border: '1px solid #ddd',
                                borderRadius: '8px'
                              }}
                            />
                          </div>
                          <Button
                            size="small"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = currentOrder.code_url;
                              link.download = `pay-qrcode-${currentOrder.order_no}.png`;
                              link.click();
                            }}
                          >
                            下载二维码
                          </Button>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ marginBottom: '12px' }}>
                            <QRCodeCanvas value={currentOrder.code_url} size={220} includeMargin={false} level="M" />
                          </div>
                          <Button
                            size="small"
                            onClick={() => {
                              const link = document.createElement('a');
                              // 生成一张画布图片供下载
                              const canvas = document.querySelector('canvas');
                              if (canvas) {
                                link.href = canvas.toDataURL('image/png');
                                link.download = `pay-qrcode-${currentOrder.order_no}.png`;
                                link.click();
                              }
                            }}
                          >
                            下载二维码
                          </Button>
                        </div>
                      )}
                      
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                        请使用{getPaymentMethodName(currentOrder.payment_method)}扫描上方二维码；也可在手机端“长按识别”或“保存到相册后识别”。
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#999' }}>
                      <div>正在生成支付二维码...</div>
                      <div style={{ fontSize: '12px', marginTop: '8px' }}>
                        请稍候，二维码将自动显示
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                  请使用{getPaymentMethodName(currentOrder.payment_method)}扫描上方二维码完成支付
                </div>
              </div>
            </Card>

            <Card title="支付说明" size="small" style={{ marginBottom: '20px' }}>
              <ol style={{ paddingLeft: '20px' }}>
                <li>使用{getPaymentMethodName(currentOrder.payment_method)}扫描上方二维码</li>
                <li>支付金额：<strong>¥{currentOrder.total_amount}</strong></li>
                <li>支付完成后，系统将自动检测并生成卡密</li>
                <li>您可以在"我的订单"页面查看卡密信息</li>
              </ol>
            </Card>

            <Form form={form} layout="vertical">
              <Form.Item label="支付备注" name="payment_note">
                <Input.TextArea 
                  placeholder="请输入支付备注信息（可选）"
                  rows={3}
                />
              </Form.Item>

              <Form.Item label="支付凭证" required>
                <Upload
                  beforeUpload={handleUploadProof}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploadLoading}
                    style={{ width: '100%' }}
                  >
                    上传支付截图
                  </Button>
                </Upload>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  请上传支付成功的截图作为凭证
                </div>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CardSales;
